import { runCreateFolderWorkflow } from "@/action-dialog";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { FilesRailDropTarget } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailDropTarget";
import {
  useFilesRailWorkspaceFiles,
  useFilesRailWorkspaceRootItems,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/useFilesRailWorkspaceFiles";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSectionFolderRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSectionFolderRow";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { useRailExpansion } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailExpansion";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  useActiveFileRecordId,
  useOpenFileRecord,
} from "@/panes/file-record-detail";
import { FilePlus, FolderPlus, LibraryBig, Upload } from "lucide-react";
import { useCallback } from "react";

/**
 * The workspace Library in the rail: an expandable row that reveals the full
 * file tree (folders down to individual files) inline. Its kebab carries the
 * creation actions (New folder / Upload files / New document), and the whole
 * row is a files drop target: dropping a file here files it at the Library
 * root. Mounted only while the workspace row is open, and the file-list query
 * waits for the Library itself to open.
 */
export function WorkspacesRailLibraryRow({
  workspaceId,
}: {
  workspaceId: string;
}) {
  const activeFileRecordId = useActiveFileRecordId();
  const openFileRecord = useOpenFileRecord();
  const menu = useRailRowContextMenu();

  const openNewFolder = useCallback(
    (currentPath: string) => {
      window.setTimeout(
        () => void runCreateFolderWorkflow({ workspaceId, currentPath }),
        0,
      );
    },
    [workspaceId],
  );

  // Fetch only once the Library is actually open (same expansion key as the
  // RailSectionFolderRow below — the shared store keeps them in sync). The
  // persisted query cache is the warmth from the second session on; eagerly
  // fetching every expanded workspace's file list on every load was cold-cache
  // compensation from before queries persisted.
  const [libraryOpen] = useRailExpansion(`ws:${workspaceId}:library`);

  const {
    fileRecords,
    fileInputRef,
    dialogs,
    actions,
    menuActions,
    uploadQueue,
    isPending: filesLoading,
    handleUploadClick,
    handleUploadToPath,
    handleFilesSelected,
    getOsDropHandler,
  } = useFilesRailWorkspaceFiles(workspaceId, { enabled: libraryOpen });

  const handleNewDocumentAtPath = useCallback(
    (parentPath: string) => {
      void uploadQueue.createDoc(parentPath, {
        onCreated: (record) => openFileRecord(record.id),
      });
    },
    [uploadQueue, openFileRecord],
  );

  const handleNewDocument = useCallback(
    () => handleNewDocumentAtPath(""),
    [handleNewDocumentAtPath],
  );

  const rootItems = useFilesRailWorkspaceRootItems({
    workspaceId,
    fileRecords,
    activeFileRecordId,
    onOpenFile: actions.openFile,
    menuActions,
    getOsDropHandler,
    onUploadToPath: handleUploadToPath,
    onCreateDocumentAtPath: handleNewDocumentAtPath,
    onCreateSubfolder: openNewFolder,
  });

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleFilesSelected}
      />
      <FilesRailDropTarget
        workspaceId={workspaceId}
        parentPath=""
        onOsDrop={getOsDropHandler("")}
      >
        <RailSectionFolderRow
          label="Library"
          icon={LibraryBig}
          expansionKey={`ws:${workspaceId}:library`}
          pending={filesLoading}
          onContextMenu={menu.onContextMenu}
          data-testid="rail-library-row"
          menu={
            <RailRowMenu {...railRowMenuProps(menu)} label="Library">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(() => openNewFolder(""), 0);
                }}
              >
                <FolderPlus />
                New folder
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(handleUploadClick, 0);
                }}
              >
                <Upload />
                Upload files
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  window.setTimeout(handleNewDocument, 0);
                }}
              >
                <FilePlus />
                New document
              </DropdownMenuItem>
            </RailRowMenu>
          }
        >
          {filesLoading ? null : rootItems.length === 0 ? (
            <RailZeroState
              icon={LibraryBig}
              accent="teal"
              variant="passive"
              title="The Library is empty."
              description="Files shared across this workspace live here."
            />
          ) : (
            rootItems
          )}
        </RailSectionFolderRow>
      </FilesRailDropTarget>
      {dialogs}
    </>
  );
}
